import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import {
  Link,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { LEARNER_INDEX_SCORES_COMPLETE } from "utility/graphQl/query";
import appInfo from "utility/app-info";
import { UrlSearchParams } from "types/enums";
import Tabs, { TabType } from "components/ui/Tabs/Tabs";
import {
  ScoreSlider,
  ContentCard,
  CardVariant,
  CardSize,
  Button,
} from "components/ui";
import { extractItem } from "components/ui/ContentCard/utils";
import { CardButtonProps } from "components/ui/ContentCard/ContentCard";
import RadarChart from "./components/radar-chart";
import ScoreDot from "./components/score-dot";
import ThemeCard from "./components/theme-card";
import ThemeModal from "./components/theme-modal";
import {
  PageContainer,
  PageContent,
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";
import { ReactComponent as PlayIcon } from "Assets/playIcon.svg";
import { trackEvent } from "utility/mixpanel";
import { useOpenModalFromCard } from "providers/content-modal-manager";
import EmptyStateImage from "Assets/images/no-assessment-radar-graph.png";
import { ReactComponent as LockIcon } from "Assets/icon/icon-lock.svg";
import clsx from "clsx";
import { useInViews } from "utility/useInViews";
import { ReactComponent as HeartIcon } from "Assets/icon/heart-icon.svg";

import styles from "./track-page-v3.module.scss";
import {
  DomainScore,
  ItemScore,
  ThemeScore,
} from "apps/user/learner/api/types";
import useFavorite from "apps/user/learner/hooks/use-favourite";
import useWebOriEnabled from "apps/user/learner/hooks/use-web-ori-enabled";
import IndexSummaryCard from "apps/user/learner/components/cards/stat-cards/IndexSummaryCard";
import OriCoachCard from "apps/user/learner/components/cards/stat-cards/OriCoachCard";
import { OriAvatar } from "components/ori";
import { useTapIn } from "apps/user/learner/lib/use-tap-in";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";
import useResumeChoice from "apps/user/learner/hooks/use-resume-choice";
import {
  REPORT_PATH,
  ReportLocationState,
} from "apps/user/learner/pages/report/reopen";

export const SCORE_THRESHOLD = 63;
const CENTER_X = 166;
const CENTER_Y = 225;
const MAX_RADIUS = 85;
const RADAR_DOTS_DIMENSIONS = {
  centerX: CENTER_X,
  centerY: CENTER_Y,
  maxRadius: MAX_RADIUS,
};

const isScored = (score?: { value?: number | null } | null): boolean =>
  typeof score?.value === "number" && Number.isFinite(score.value);

interface TakeYourAssessmentInfoProps {
  hasResumableProgress: boolean;
}

const TakeYourAssessmentInfo = ({
  hasResumableProgress,
}: TakeYourAssessmentInfoProps) => {
  const { t } = useTranslation(["index", "components"]);
  const { ref, inView } = useInViews();
  const location = useLocation();

  const buttonLabel = hasResumableProgress
    ? "Resume my assessment"
    : "Take my assessment";

  const logImpression = useCallback(() => {
    trackEvent("Take Assessment Card Impression", {
      "Button Label": buttonLabel,
      "Has Resumable Progress": hasResumableProgress,
      "Current Domain": window.location.origin,
      Destination: location.pathname,
    });
  }, [buttonLabel, hasResumableProgress, location.pathname]);
  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);
  const navigate = useNavigate();
  const { requestResume, resumeChoiceModal } = useResumeChoice();
  const handleBeginAssessment = () => {
    trackEvent("Take Assessment Clicked", {
      "Button Label": buttonLabel,
      "Has Resumable Progress": hasResumableProgress,
      "Current Domain": window.location.origin,
      Destination: location.pathname,
    });
    requestResume(hasResumableProgress, () =>
      navigate("/user/woneindex/assessment")
    );
  };
  return (
    <div className={styles["themes-fade-gradient"]} ref={ref}>
      {resumeChoiceModal}
      <div className={styles["cta-container"]}>
        <LockIcon width={24} height={24} />
        <p className={styles["take-assessment-info"]}>
          {hasResumableProgress
            ? t("index:trackPage.resumeStateSubtitle")
            : t("index:trackPage.takeAssessmentInfo")}
        </p>
        <Button variant="enclosed-primary" onClick={handleBeginAssessment}>
          {hasResumableProgress
            ? t("index:trackPage.resumeAssessment")
            : t("components:indexAssessment.cta")}
        </Button>
      </div>
    </div>
  );
};

interface RecommendedContentProps {
  domain: DomainScore;
  getActionButtons: (
    itemId: string,
    itemTitle: string,
    listName: string,
    listPosition: number,
    isFavourite: boolean
  ) => CardButtonProps[];
}

const RecommendedContent: React.FC<RecommendedContentProps> = ({
  domain,
  getActionButtons,
}) => {
  const { t } = useTranslation("index");
  const { ref, inView } = useInViews();

  const logImpression = useCallback(() => {
    trackEvent("Track Page Recommendation Recordings Impression", {});
  }, []);
  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  return (
    <section className={styles["recommended-section"]} ref={ref}>
      <div className={styles["recommended-header"]}>
        <p className={styles["recommended-title"]}>
          {t("index:trackPage.sessionsFor", { domain: domain.name })}
        </p>
      </div>

      <div className={styles["recommended-grid"]}>
        {domain.recommendedRecordings.map((rec, index: number) => {
          const cardItem = extractItem(rec.recording);
          const listName = `TrackRecommended-${domain.name}`;

          return (
            <div key={cardItem.id} className={styles["card-wrapper"]}>
              <ContentCard
                item={cardItem}
                variant={CardVariant.IMAGE}
                size={CardSize.REGULAR}
                listName={listName}
                listPosition={index + 1}
                actionButtons={getActionButtons(
                  cardItem.id,
                  cardItem.title,
                  listName,
                  index + 1,
                  cardItem.isFavourite || false
                )}
                widthMode="full"
              />
              {rec.subTitle && (
                <p className={styles["card-subtitle"]}>{rec.subTitle}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const scoreProps = (score?: number | null, bandKey?: string | null) => ({
  ...(score != null ? { Score: score } : {}),
  ...(score != null && bandKey ? { "Band Key": bandKey } : {}),
});

const TrackPageV3: React.FC = () => {
  const { t } = useTranslation(["index", "components"]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestResume, resumeChoiceModal } = useResumeChoice();

  const openModalFromCard = useOpenModalFromCard();

  // `all` rather than Apollo's default: one failing resolver otherwise
  // discards the entire response, and a tab full of scores that arrived
  // perfectly well goes dark because a single card could not be built.
  // Keeping the partial data costs the tab that card instead.
  const { data, loading, error } = useQuery(LEARNER_INDEX_SCORES_COMPLETE, {
    variables: {
      appInfo,
      historyLimit: 1,
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const indexScores = data?.learner?.indexScores;

  // Memoize data arrays
  const domains: DomainScore[] = useMemo(
    () => indexScores?.domains || [],
    [indexScores?.domains]
  );
  const themes: ThemeScore[] = useMemo(
    () => indexScores?.themes || [],
    [indexScores?.themes]
  );
  const items: ItemScore[] = useMemo(
    () => indexScores?.items || [],
    [indexScores?.items]
  );
  const overallIndex = indexScores?.overallIndex;
  const takenAt = indexScores?.takenAt;

  // Saved progress past its expiry window already reads as false server-side,
  // so a stale attempt never surfaces the resume prompt.
  const hasResumableProgress = Boolean(
    data?.learner?.assessmentSchedule?.hasResumableProgress
  );
  const summary = indexScores?.summary;

  // What separates a tab missing a card from a tab with nothing on it.
  const hasOverallScore = isScored(overallIndex);
  const hasScoreData = hasOverallScore || domains.length > 0;
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();
  const { enabled: showOriWeb } = useWebOriEnabled();
  // Flags arrive on their own query: a view reported before they resolve
  // would call the entry point hidden when it is merely unknown.
  const { enabled: tapInEnabled, flagsResolved } = useTapIn();

  // Only a generated report is worth going back to: the score panel opens it
  // when there is one, and stays as it was otherwise.
  const reportReady = indexScores?.report?.status === "READY";

  // Get active domain from URL or default to first domain
  const activeDomainId =
    searchParams.get(UrlSearchParams.Domain) || domains[0]?.shorthand;

  // Get theme modal ID from URL
  const themeModalId = searchParams.get(UrlSearchParams.Theme);
  const themeModal = themeModalId
    ? themes.find((t) => t.id === themeModalId)
    : null;

  // Filter items for theme modal
  const themeItems = useMemo(() => {
    if (!themeModal) return [];
    return items.filter((item) => item.themeShorthand === themeModal.shorthand);
  }, [items, themeModal]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return domains.map((domain: DomainScore) => ({
      domain: domain.name,
      score: domain.value,
      fullMark: 100,
    }));
  }, [domains]);

  // Set initial domain if not in URL
  useEffect(() => {
    if (!searchParams.get(UrlSearchParams.Domain) && domains.length > 0) {
      const params = new URLSearchParams(location.search);
      params.set(UrlSearchParams.Domain, domains[0].shorthand);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [domains, searchParams, navigate, location]);

  const handleThemeClick = useCallback(
    (themeId: string) => {
      const params = new URLSearchParams(location.search);
      params.set(UrlSearchParams.Theme, themeId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [location.search, location.pathname, navigate]
  );

  const handleDomainChange = useCallback(
    (domainId: string) => {
      const prevDomainId =
        searchParams.get(UrlSearchParams.Domain) || domains[0]?.shorthand;
      const prevDomain = domains.find((d) => d.shorthand === prevDomainId);
      const newDomain = domains.find((d) => d.shorthand === domainId);
      const domainIndex = domains.findIndex((d) => d.shorthand === domainId);

      const params = new URLSearchParams(location.search);
      params.set(UrlSearchParams.Domain, domainId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });

      trackEvent("Domain Changed", {
        "Previous Domain": prevDomain?.shorthand || null,
        "New Domain": newDomain?.shorthand || null,
        "Previous Domain Score": prevDomain?.value ?? null,
        "New Domain Score": newDomain?.value ?? null,
        "Domain Index": domainIndex,
        "Total Domains": domains.length,
        "Is Empty State": !takenAt,
      });
    },
    [
      searchParams,
      location.search,
      location.pathname,
      navigate,
      domains,
      takenAt,
    ]
  );

  // Get action buttons for content cards
  const getActionButtons = useCallback(
    (
      itemId: string,
      itemTitle: string,
      listName: string,
      listPosition: number,
      isFavourite: boolean
    ): CardButtonProps[] => {
      const buttons: CardButtonProps[] = [
        {
          icon: <PlayIcon />,
          ariaLabel: "Watch Now",
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            trackEvent("Recording Play Button Clicked", {
              "Recording ID": itemId,
              "Recording Title": itemTitle,
              "List Name": listName,
              "List Position": listPosition,
              "Current Page": window.location.href,
            });
            openModalFromCard({ type: "recording", id: itemId }, true);
          },
        },
        {
          icon: (
            <HeartIcon
              stroke={
                isFavourite ? styles["love-red"] : styles["dark-shade-grey"]
              }
              color={isFavourite ? styles["love-red"] : "transparent"}
            />
          ),
          ariaLabel: isFavourite
            ? "Remove from favourites"
            : "Add to favourites",
          loading: isFavouriteLoading,
          onClick: (e, itemId) => {
            e.preventDefault();
            onFavourite({
              id: itemId,
              isFavourite: isFavourite,
              __typename: "LearnerRecording",
            });
          },
        },
      ];

      return buttons;
    },
    [openModalFromCard, onFavourite, isFavouriteLoading]
  );

  // Create tabs from domains
  const domainTabs: TabType[] = useMemo(() => {
    return domains.map((domain) => {
      const domainThemesForTab = themes.filter(
        (theme) => theme.domainShorthand === domain.shorthand
      );

      return {
        key: domain.shorthand,
        label: domain.name,
        icon: (props) => <ScoreDot value={domain.value} {...props} />,
        content: (
          <div
            className={styles["domain-content"]}
            id={`domain-panel-${domain.shorthand}`}
            role="tabpanel"
          >
            {/* Domain Overview */}
            <div className={styles["domain-overview"]}>
              {takenAt ? (
                <div className={styles["domain-score"]}>
                  <span className={styles["domain-score-value"]}>
                    {domain.value}
                  </span>
                  <span className={styles["domain-score-label"]}>
                    {domain.label}
                  </span>
                </div>
              ) : (
                <p className={styles["domain-no-score"]}>-</p>
              )}
              <p className={styles["domain-description"]}>{domain.blurb}</p>
            </div>

            {/* Theme Cards */}
            {domainThemesForTab.length > 0 && (
              <section className={styles["themes-section"]}>
                <div className={styles["themes-header"]}>
                  <p className={styles["themes-title"]}>
                    {t("index:trackPage.themeBreakdownTitle")}
                  </p>
                  <p className={styles["themes-count"]}>
                    {t("index:trackPage.themesCount", {
                      themeCount: domainThemesForTab.length,
                    })}
                  </p>
                </div>
                <div className={styles["themes-grid"]}>
                  {domainThemesForTab.map((theme, index) => {
                    // Filter items for this theme
                    const themeItems = items.filter(
                      (item) => item.themeShorthand === theme.shorthand
                    );

                    return (
                      <ThemeCard
                        key={theme.id}
                        id={theme.id}
                        name={theme.name}
                        label={theme.label || ""}
                        labelKey={theme.labelKey}
                        value={theme.value}
                        items={themeItems}
                        onClick={handleThemeClick}
                        takenAt={takenAt}
                        domainShorthand={domain.shorthand}
                        domainScore={domain.value}
                        themePosition={index + 1}
                        themeShorthand={theme.shorthand}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {/* Recommended Content */}
            {takenAt &&
              domain.recommendedRecordings &&
              domain.recommendedRecordings.length > 0 && (
                <RecommendedContent
                  domain={domain}
                  getActionButtons={getActionButtons}
                />
              )}
          </div>
        ),
      };
    });
  }, [domains, themes, items, handleThemeClick, getActionButtons, takenAt, t]);

  useEffect(() => {
    if (!activeDomainId) return;
    const activeDomain = domains.find((d) => d.shorthand === activeDomainId);
    if (!activeDomain) return;
    const domainIndex = domains.findIndex(
      (d) => d.shorthand === activeDomainId
    );
    const themeCount = themes.filter(
      (t) => t.domainShorthand === activeDomain.shorthand
    ).length;

    trackEvent("Domain Viewed", {
      "Domain Shorthand": activeDomain.shorthand,
      "Domain Score Value": activeDomain.value ?? null,
      "Domain Score Band": activeDomain.labelKey ?? null,
      "Domain Index": domainIndex,
      "Empty State": activeDomain.value === null,
      "Theme Count": themeCount,
    });
  }, [activeDomainId, domains, themes]);

  // The `Ori Tap-In Index Viewed` event (Mixpanel) counts only what the page
  // renders: not the spinner of a cached revisit, nor a no-data error screen.
  const showingScores =
    !loading && Boolean(indexScores) && !(error && !hasScoreData);

  // Once per page load.
  const indexViewedRef = useRef(false);
  useEffect(() => {
    if (!flagsResolved || !showingScores || indexViewedRef.current) return;
    indexViewedRef.current = true;
    trackEvent("Ori Tap-In Index Viewed", {
      "Has Score": hasOverallScore,
      ...scoreProps(
        hasOverallScore ? overallIndex?.value : null,
        overallIndex?.labelKey
      ),
      "Ask Ori Shown": tapInEnabled && Boolean(takenAt) && hasOverallScore,
    });
  }, [
    flagsResolved,
    showingScores,
    hasOverallScore,
    overallIndex,
    tapInEnabled,
    takenAt,
  ]);

  // One report per distinct failure rather than one per render, and one again
  // if a retry lands on a different failure. The resolver's own words are
  // what says which part of the tab came back empty, so they go in the
  // reported message.
  const reportedFailure = useRef<string>();
  useEffect(() => {
    if (!error) {
      reportedFailure.current = undefined;
      return;
    }

    const detail = error.graphQLErrors.length
      ? error.graphQLErrors
          .map((graphQlError) => graphQlError.message)
          .join("; ")
      : error.message;
    if (reportedFailure.current === detail) return;

    reportedFailure.current = detail;
    logError(`Could not load the learner's WONE index: ${detail}`, error);
  }, [error]);

  if (loading) {
    return (
      <div className={styles["error-loading-screen"]}>
        <PageLoading />
      </div>
    );
  }

  // A failure that took one card leaves the rest of the tab standing. A
  // failure that took the scores themselves has nothing to draw, and gets the
  // error treatment the report page shares: the two screens read one query.
  if (!indexScores || (error && !hasScoreData)) {
    return (
      <div className={styles["error-loading-screen"]}>
        <PageError />
      </div>
    );
  }

  const handleBeginAssessment = () => {
    trackEvent("Take Assessment Clicked", {
      "Button Label": hasResumableProgress
        ? "Resume my assessment"
        : "Take my assessment",
      "Has Resumable Progress": hasResumableProgress,
      "Current Domain": window.location.origin,
      Destination: location.pathname,
    });
    requestResume(hasResumableProgress, () =>
      navigate("/user/woneindex/assessment")
    );
  };

  const reopenedReport: ReportLocationState = { reopened: true };
  // Laid over the score and the radar rather than wrapped around them, so
  // the panel keeps its layout and the link holds no other controls.
  const reportLink = reportReady ? (
    <Link
      to={REPORT_PATH}
      state={reopenedReport}
      className={styles["score-panel-link"]}
      aria-label={t("index:trackPage.openReport")}
      data-testid="score-panel-report-link"
    />
  ) : null;

  return (
    <PageContainer
      className={styles["track-page-container"]}
      background="transparent"
    >
      {resumeChoiceModal}
      <PageContent maxWidth="100%">
        <div className={styles["track-page"]}>
          <aside className={styles.sidebar}>
            <div className={styles["score-section"]}>
              {takenAt && showOriWeb ? (
                <div className={styles["ori-score-column"]}>
                  {(hasOverallScore || chartData.length > 0) && (
                    <div
                      className={clsx(
                        styles["score-panel"],
                        reportLink && styles["score-panel--linked"]
                      )}
                    >
                      {hasOverallScore && (
                        <IndexSummaryCard
                          overallIndexScore={overallIndex}
                          flush
                        />
                      )}

                      {chartData.length > 0 && (
                        <>
                          <div className={styles["sidebar-separator"]} />
                          <div className={styles["chart-wrapper"]}>
                            <RadarChart
                              chartData={chartData}
                              dotsDimensions={RADAR_DOTS_DIMENSIONS}
                            />
                          </div>
                        </>
                      )}
                      {reportLink}
                    </div>
                  )}

                  <div className={styles["sidebar-separator"]} />
                  <OriCoachCard
                    overallIndexScore={overallIndex}
                    summary={summary}
                    takenAt={takenAt}
                    flush
                    source="Ori Coach Card (Track)"
                  />
                </div>
              ) : takenAt ? (
                <div
                  className={clsx(
                    styles["score-section-content"],
                    reportLink && styles["score-panel--linked"]
                  )}
                >
                  {chartData.length > 0 && (
                    <div className={styles["chart-wrapper"]}>
                      <RadarChart
                        chartData={chartData}
                        dotsDimensions={RADAR_DOTS_DIMENSIONS}
                      />
                    </div>
                  )}

                  {hasOverallScore && (
                    <div className={styles["index-data"]}>
                      <div className={styles["overall-score"]}>
                        <span className={styles["score-value"]}>
                          {overallIndex?.value}
                        </span>
                        <span className={styles["score-label"]}>
                          {overallIndex?.label}
                        </span>
                      </div>

                      <div className={styles["score-slider-container"]}>
                        <ScoreSlider
                          value={overallIndex?.value}
                          barColor="white"
                          textColor="white"
                          showScore={false}
                        />
                      </div>

                      {overallIndex?.blurb && (
                        <p className={styles["score-description"]}>
                          {overallIndex.blurb}
                        </p>
                      )}
                    </div>
                  )}
                  {reportLink}
                </div>
              ) : (
                <>
                  <div className={styles["empty-state-score-section-content"]}>
                    <img
                      src={EmptyStateImage}
                      alt="No assessment taken"
                      className={styles["empty-state-radar-image"]}
                    />
                    <div
                      className={clsx(
                        styles["empty-state-score-section-text"],
                        showOriWeb &&
                          styles["empty-state-score-section-text--ori"]
                      )}
                    >
                      <p className={styles["empty-state-score-section-header"]}>
                        {hasResumableProgress
                          ? t("index:trackPage.resumeStateHeader")
                          : t("index:trackPage.emptyStateHeader")}
                      </p>
                      <div className={styles["empty-state-ori-group"]}>
                        {showOriWeb && (
                          <div className={styles["empty-state-ori-identity"]}>
                            <OriAvatar size={48} />
                            <span className={styles["empty-state-ori-label"]}>
                              {t("index:trackPage.oriLabel")}
                            </span>
                          </div>
                        )}
                        <p
                          className={
                            styles["empty-state-score-section-description"]
                          }
                        >
                          {hasResumableProgress
                            ? t("index:trackPage.resumeStateSubtitle")
                            : t("index:trackPage.emptyStateSubtitle")}
                        </p>
                      </div>
                      <div
                        className={styles["empty-state-score-section-buttons"]}
                      >
                        <Button
                          variant="enclosed-primary"
                          onClick={handleBeginAssessment}
                          monochromeColour="white"
                          fullWidth
                        >
                          {hasResumableProgress
                            ? t("index:trackPage.resumeAssessment")
                            : t("components:indexAssessment.cta")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>

          <main
            className={clsx(
              styles["main-content"],
              !takenAt && styles["main-content-unscrollable"]
            )}
          >
            {domainTabs.length > 0 && (
              <Tabs
                tabs={domainTabs}
                activeTab={activeDomainId || domains[0]?.shorthand}
                onTabChange={handleDomainChange}
                tabsClassName={styles["domain-tabs-wrapper"]}
                contentClassName={styles["domain-tab-content"]}
                showScrollButtons
              />
            )}
            {!takenAt && (
              <TakeYourAssessmentInfo
                hasResumableProgress={hasResumableProgress}
              />
            )}
          </main>

          {themeModal && themeItems.length > 0 && (
            <ThemeModal
              themeId={themeModal.id}
              themeName={themeModal.name}
              themeBlurb={themeModal.blurb || ""}
              items={themeItems}
              themeShorthand={themeModal.shorthand}
              themeScore={themeModal.value}
              themeLabelKey={themeModal.labelKey}
              domainShorthand={themeModal.domainShorthand || activeDomainId}
            />
          )}
        </div>
      </PageContent>
    </PageContainer>
  );
};

export default TrackPageV3;
